import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { LyraAvatar } from '@/components/LyraAvatar';
import { useAuth } from '@/contexts/AuthContext';
import {
  validateLicense,
  saveLicenseToStorage,
  hasValidLicenseInStorage,
  formatLicenseKey,
  getLicenseErrorMessage,
  LicenseValidationResult
} from '@/services/licenseService';

export const Redeem = () => {
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (user) {
      navigate('/micro-dashboard');
    }
  }, [user, navigate]);

  // If there's already a valid license in storage, proceed to onboarding
  useEffect(() => {
    if (hasValidLicenseInStorage()) {
      navigate('/onboarding');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const formattedKey = formatLicenseKey(licenseKey);
      const result: LicenseValidationResult = await validateLicense(formattedKey);

      if (result.valid) {
        // Save license data for use during signup
        saveLicenseToStorage(result);

        toast({
          title: "License verified!",
          description: `Welcome, ${result.customerName || 'there'}! Let's get you set up.`
        });

        // Proceed to onboarding
        navigate('/onboarding');
      } else {
        setError(getLicenseErrorMessage(result.errorCode));
      }
    } catch (err) {
      console.error('License validation error:', err);
      setError('Unable to verify your license. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow users to type naturally, we'll format on submit
    setLicenseKey(e.target.value);
    // Clear error when user starts typing
    if (error) setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-xl bg-white/80 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <LyraAvatar />
          </div>
          <CardTitle className="text-2xl font-bold">
            Activate Your License
          </CardTitle>
          <CardDescription className="text-base">
            Enter your license key from your Good Academy purchase to get started
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="license-key">License Key</Label>
              <Input
                id="license-key"
                type="text"
                value={licenseKey}
                onChange={handleKeyChange}
                required
                placeholder="LYRA-XXXX-XXXX-XXXX"
                className={`font-mono text-center text-lg tracking-wider ${error ? 'border-red-500' : ''}`}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              {error && (
                <p className="text-sm text-red-600 mt-2">
                  {error}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-lg font-semibold py-3"
              disabled={loading || !licenseKey.trim()}
            >
              {loading ? 'Verifying...' : 'Verify License'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Don't have a license?{' '}
              <a
                href="https://goodacademy.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-600 hover:text-cyan-700 font-medium"
              >
                Purchase access
              </a>
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/auth?signin=true')}
                className="text-cyan-600 hover:text-cyan-700 font-medium"
              >
                Sign in
              </button>
            </p>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 text-center">
              Your license key was sent to your email after purchase.
              Check your inbox (and spam folder) for an email from Good Academy.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Redeem;
